/**
 * Corner-handle placement and pickup, shared by the construction Round sub-tool
 * (#100) and the leather Round sub-tool (#974).
 *
 * Round is a handle gesture: a dot sits on each roundable corner's interior
 * bisector, and where that dot sits encodes the corner's CURRENT radius. Two
 * places have to agree on that position exactly — the renderer that draws it
 * and the hit-test that picks it up — and now two tools do as well. The rule
 * lived inline in `RoundController`; #974 needed the identical scan over part
 * and cutout outlines, so it moved here rather than being copied (#990).
 *
 * The arithmetic itself still comes from `constructionRounding.ts`
 * (`getCornerGeometry` / `handleDistanceFromCorner`), which is already
 * pathData-based. What this module adds is the SEARCH: which corners exist,
 * where each one's handle currently sits, and which handle a click lands on.
 *
 * Outlines are addressed by an opaque `key` the caller chooses — a construction
 * line id, or a leather `piece:<id>` / `cutout:<id>` tag — so neither model type
 * leaks in here.
 */
import type paper from "paper";
import {
  getCornerGeometry,
  handleDistanceFromCorner,
  type CornerGeometry,
  type CornerRoundingEntry,
} from "./constructionRounding";
import {
  currentPointerKind,
  isCoarsePointer,
} from "../canvas/pointerPrecision";

/** Below this a radius counts as no rounding at all: corners tighter than it
 *  can't be filleted, and a drag that lands under it un-rounds instead. */
export const ROUND_MIN_RADIUS_MM = 0.5;

/** Pickup radius for a corner handle, in screen px. */
export const CORNER_HANDLE_TOLERANCE_PX = 12;

/** One outline offered to the scan, with whatever rounding it already carries. */
export interface RoundableOutline {
  key: string;
  /** The UN-rounded source. Corners stay addressable at their original
   *  positions this way, even after some of them have been rounded. */
  sharpPathData: string;
  corners: ReadonlyArray<CornerRoundingEntry>;
  /** Ties inside the pickup tolerance go to a preferred outline — the selected
   *  one, so a handle on what you're working on wins over an overlapping
   *  neighbour's (#262). */
  preferred?: boolean;
}

export interface CornerHandleHit {
  key: string;
  pathIndex: number;
  segmentIndex: number;
  geom: CornerGeometry;
  handlePoint: { x: number; y: number };
  /** The corner's stored radius, or undefined when it isn't rounded yet. A
   *  caller seeds its drag from this so an immediate release re-commits the
   *  radius the user could already see. */
  existingRadius: number | undefined;
  distance: number;
}

/**
 * Where a corner's handle dot sits for a given radius: out along the interior
 * bisector, at the centre of the arc that radius would inscribe. A radius of 0
 * puts it at its rest position on the corner itself.
 */
export function cornerHandlePoint(
  geom: CornerGeometry,
  radiusMm: number,
): { x: number; y: number } {
  const dist = handleDistanceFromCorner(geom, radiusMm);
  return {
    x: geom.cornerPoint.x + geom.bisectorDir.x * dist,
    y: geom.cornerPoint.y + geom.bisectorDir.y * dist,
  };
}

/**
 * The radius a corner's handle is currently DRAWN at, following the hierarchy
 * both the renderer and the hit-test must obey: an existing entry wins; failing
 * that a panel preset, but only if it actually fits the corner; otherwise rest.
 *
 * A too-big preset is suppressed rather than clamped, so the dot stays put
 * instead of jumping to a radius the commit would refuse anyway.
 */
export function displayRadiusFor(
  geom: CornerGeometry,
  existingRadius: number | undefined,
  presetRadiusMm: number | null,
): number {
  const presetFits =
    presetRadiusMm !== null &&
    isFinite(presetRadiusMm) &&
    presetRadiusMm > 0 &&
    presetRadiusMm <= geom.maxRadius;
  const base = existingRadius ?? (presetFits ? presetRadiusMm! : 0);
  return Math.min(base, geom.maxRadius);
}

/**
 * Find the corner handle nearest `pt` across `outlines`, or null when none is
 * within the pickup tolerance. Corners too tight to fillet at all are skipped.
 */
export function hitCornerHandle(
  outlines: ReadonlyArray<RoundableOutline>,
  pt: { x: number; y: number },
  scope: paper.PaperScope,
  opts: { presetRadiusMm: number | null },
): CornerHandleHit | null {
  // A finger reaches twice as far (#1150) — not the handles' 4×: 12 px is
  // already generous, and a press that misses a handle selects the part
  // instead, so a wider reach would swallow those presses.
  const reachPx = isCoarsePointer(currentPointerKind())
    ? CORNER_HANDLE_TOLERANCE_PX * 2
    : CORNER_HANDLE_TOLERANCE_PX;
  const tolerance = reachPx / (scope.view?.zoom ?? 1);
  let bestAny: CornerHandleHit | null = null;
  let bestPreferred: CornerHandleHit | null = null;

  for (const outline of outlines) {
    if (!outline.sharpPathData) continue;
    let cp: paper.CompoundPath;
    try {
      cp = new scope.CompoundPath({
        pathData: outline.sharpPathData,
        insert: false,
      });
    } catch {
      continue;
    }
    const radiusByKey = new Map<string, number>();
    for (const c of outline.corners) {
      radiusByKey.set(`${c.pathIndex}:${c.segmentIndex}`, c.radius);
    }
    const subpaths: paper.Path[] =
      cp.children && cp.children.length > 0
        ? (cp.children as paper.Path[])
        : [cp as unknown as paper.Path];
    for (let pi = 0; pi < subpaths.length; pi++) {
      const sub = subpaths[pi]!;
      for (let si = 0; si < sub.segments.length; si++) {
        const geom = getCornerGeometry(outline.sharpPathData, pi, si, scope);
        if (!geom) continue;
        if (geom.maxRadius < ROUND_MIN_RADIUS_MM) continue;
        const existingRadius = radiusByKey.get(`${pi}:${si}`);
        const handlePoint = cornerHandlePoint(
          geom,
          displayRadiusFor(geom, existingRadius, opts.presetRadiusMm),
        );
        const distance = Math.hypot(handlePoint.x - pt.x, handlePoint.y - pt.y);
        if (distance > tolerance) continue;
        const hit: CornerHandleHit = {
          key: outline.key,
          pathIndex: pi,
          segmentIndex: si,
          geom,
          handlePoint,
          existingRadius,
          distance,
        };
        if (!bestAny || distance < bestAny.distance) bestAny = hit;
        if (
          outline.preferred &&
          (!bestPreferred || distance < bestPreferred.distance)
        ) {
          bestPreferred = hit;
        }
      }
    }
    cp.remove();
  }
  return bestPreferred ?? bestAny;
}

/**
 * Merge one corner's radius into an outline's rounding entries.
 *
 * A radius below `ROUND_MIN_RADIUS_MM` REMOVES the corner's entry instead —
 * that's how a drag pulled back past the corner un-rounds it. Returns the
 * entries to store; an empty array means the outline is pristine again and the
 * caller should drop the whole `cornerRounding` block.
 */
export function mergeCornerRadius(
  corners: ReadonlyArray<CornerRoundingEntry>,
  pathIndex: number,
  segmentIndex: number,
  radius: number,
): CornerRoundingEntry[] {
  const rest = corners.filter(
    (c) => !(c.pathIndex === pathIndex && c.segmentIndex === segmentIndex),
  );
  if (radius < ROUND_MIN_RADIUS_MM) return rest;
  return [...rest, { pathIndex, segmentIndex, radius }];
}
